// Trang Lịch sử — MỘT dòng thời gian duy nhất cho mọi thứ người dùng đã xem.
//
// Sáu nguồn, sáu bảng, sáu kiểu phân trang riêng: phiên trò chuyện (`/v2/chat/sessions`),
// lá số Tử Vi, lá số Bát Tự, bài chỉ tay, phiên Bói Live, lượt đoán giờ sinh. Không có
// endpoint gộp, nên việc gộp làm ở FE: mỗi lượt tải xin CÙNG một số trang ở mọi nguồn
// đang bật rồi trộn theo thời gian.
//
// ⚠️ Vì thế danh sách đã tải phải được SẮP LẠI sau mỗi lượt "Xem thêm" (xem
// `sortFeedByDate`): trang 2 của nguồn A có thể mới hơn đuôi trang 1 của nguồn B, nên
// nối đuôi thuần tuý cho ra thứ tự sai.

import {
  deleteBaziChart,
  deleteBirthTimeHistory,
  deleteLiveSession,
  deletePalmReading,
  deleteSession,
  deleteTuviChart,
  listBaziCharts,
  listBirthTimeHistory,
  listLiveSessions,
  listPalmReadings,
  isPalmDraftItem,
  palmStageLabel,
  listSessions,
  listTuviCharts,
  normalizeSearchText,
  type SessionSummary,
} from "./boide-api";

/**
 * Tag của một dòng.
 *
 * 🔴 Suy từ NỘI DUNG, không từ nguồn: một phiên Kinh Dịch có quẻ nhưng 0 tin nhắn là
 * người gieo xong mà số dư không đủ để luận giải — gọi nó là "Đàm đạo" thì sai, vì
 * chưa có câu nào được nói. Chính những dòng ấy là thứ người dùng đi tìm sau khi nạp
 * tiền, nên tag phải nói đúng trạng thái của nó.
 */
export type FeedCategory = "luan-giai" | "dam-dao";

/** Loại hình huyền học của một dòng — dùng cho nhãn, icon và bộ lọc. */
export type FeedType = "iching" | "tuvi" | "bazi" | "palm" | "tong_hop" | "live" | "birth_time";

/**
 * Nguồn dữ liệu của một dòng — quyết định cách MỞ LẠI và cách XOÁ.
 *
 * ⚠️ Khác `type`: một dòng `palm` có thể là bài luận chỉ tay (`palm_reading`) hoặc phiên
 * hỏi đáp về bàn tay ấy (`session`). Hai thứ ở hai bảng, xoá bằng hai endpoint.
 */
export type FeedSource =
  "session" | "tuvi_chart" | "bazi_chart" | "palm_reading" | "live_session" | "birth_time_probe";

export type FeedItem = {
  /** Khoá duy nhất trong danh sách ĐÃ GỘP — id của sáu nguồn là sáu không gian riêng. */
  key: string;
  source: FeedSource;
  id: string;
  category: FeedCategory;
  type: FeedType;
  title: string;
  /** Các mẩu thông tin phụ, đã chuẩn hoá — mỗi nguồn có dữ kiện riêng. */
  details: string[];
  createdAt: string;
  /** Bài luận chỉ tay của phiên (chỉ phiên chỉ tay mới có) — mở bằng id này, không bằng phiên. */
  palmReadingId?: string;
  /**
   * Lượt chỉ tay ĐÃ TRẢ TIỀN nhưng chưa ra bài — mở bằng `?draft=` để đi tiếp, không
   * phải `?reading=` (id ấy chưa có bài luận nào để tải, mở ra là một trang trống).
   */
  palmDraft?: boolean;
  /** Chủ đề Bói Đê của phiên — mở thẳng trang chủ đề tương ứng. */
  boiDeSlug?: string;
  /** Chỉ phiên trò chuyện mới đổi tên được; các nguồn còn lại không có endpoint đặt tên. */
  renamable: boolean;
};

export type FeedPage = {
  items: FeedItem[];
  /** Tổng bản ghi của MỌI nguồn đang bật — con số thật, chưa trừ bộ lọc chạy ở client. */
  total: number;
  hasMore: boolean;
  /**
   * Mốc thời gian mà danh sách GỘP còn đảm bảo ĐẦY ĐỦ tới đó. `null` = mọi nguồn đã hết,
   * không còn gì thiếu.
   *
   * 🔴 Đây là thứ giữ cho phân trang không nói dối. Sáu nguồn phân trang độc lập: sau khi
   * xin trang 1 của tất cả, nguồn A có thể đã chạm mốc 01/03 còn nguồn B mới tới 20/03 —
   * mọi bản ghi của B trong khoảng 20/03…01/03 CHƯA được tải. Cắt danh sách gộp tại
   * `sealedUntil` (= mốc MỚI NHẤT trong các đuôi của những nguồn còn dữ liệu) thì phần
   * còn lại là một tiền tố hoàn chỉnh thật sự — không có mục nào của trang 2 lẽ ra phải
   * nằm ở trang 1.
   *
   * Không có nó thì bấm "Sau" xong bấm "Trước" có thể ra một danh sách khác hẳn.
   */
  sealedUntil: string | null;
};

/** Mỗi nguồn lấy bao nhiêu bản ghi cho một lượt tải. */
export const FEED_SOURCE_PAGE_SIZE = 10;

/** Bao nhiêu dòng trên MỘT trang hiển thị — khác hẳn số trên, xem `sealedUntil`. */
export const FEED_PAGE_SIZE = 10;

export const CATEGORY_LABELS: Record<FeedCategory, string> = {
  "luan-giai": "Luận giải",
  "dam-dao": "Đàm đạo",
};

export const TYPE_LABELS: Record<FeedType, string> = {
  iching: "Kinh Dịch",
  tuvi: "Tử Vi",
  bazi: "Bát Tự",
  palm: "Chỉ tay",
  tong_hop: "Bói Đê",
  live: "Bói Live",
  birth_time: "Giờ sinh",
};

export type CategoryFilter = "all" | FeedCategory;
export type TypeFilter = "all" | FeedType;

export const CATEGORY_TABS: { key: CategoryFilter; label: string }[] = [
  { key: "all", label: "Tất cả" },
  { key: "luan-giai", label: CATEGORY_LABELS["luan-giai"] },
  { key: "dam-dao", label: CATEGORY_LABELS["dam-dao"] },
];

const TYPE_ORDER: FeedType[] = ["iching", "tuvi", "bazi", "palm", "tong_hop", "live", "birth_time"];

/**
 * Những loại hình CÓ THỂ xuất hiện dưới một tag.
 *
 * Bói Live không bao giờ là Đàm đạo (nó là bản ghi một phiên nói, không phải phiên
 * chat), Bói Đê không bao giờ là Luận giải (nó chỉ tồn tại dưới dạng phiên chat), và
 * đoán giờ sinh cũng không bao giờ là Đàm đạo — nó là một lượt trắc nghiệm đã chấm,
 * không mở ra phiên hỏi đáp nào. Ẩn hẳn những chip ấy thay vì để người dùng chọn một
 * tổ hợp luôn rỗng rồi tưởng mất dữ liệu.
 */
const TYPES_BY_CATEGORY: Record<CategoryFilter, FeedType[]> = {
  all: TYPE_ORDER,
  "luan-giai": ["iching", "tuvi", "bazi", "palm", "live", "birth_time"],
  "dam-dao": ["iching", "tuvi", "bazi", "palm", "tong_hop"],
};

export function typeTabsFor(category: CategoryFilter): { key: TypeFilter; label: string }[] {
  return [
    { key: "all" as const, label: "Tất cả" },
    ...TYPES_BY_CATEGORY[category].map((type) => ({
      key: type as TypeFilter,
      label: TYPE_LABELS[type],
    })),
  ];
}

/** Bộ lọc loại hình có còn nghĩa dưới tag mới không — không thì phải kéo về "Tất cả". */
export function isTypeAllowed(category: CategoryFilter, type: TypeFilter): boolean {
  return type === "all" || TYPES_BY_CATEGORY[category].includes(type);
}

const GENDER_LABELS: Record<string, string> = { nam: "Nam", nu: "Nữ", nữ: "Nữ" };

function genderLabel(value: string): string {
  return GENDER_LABELS[value] ?? value;
}

function formatVnd(amount: number): string {
  return `${Math.round(amount).toLocaleString("vi-VN")}đ`;
}

function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.max(0, Math.round(totalSeconds % 60));
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Nhãn kết thúc của phiên Bói Live. Giữ đồng bộ với bảng cùng tên ở màn Bói Live —
 * hai chữ khác nhau cho cùng một phiên đọc ra là hai phiên khác nhau.
 */
const LIVE_END_REASON_LABELS: Record<string, string> = {
  user_ended: "Hoàn tất",
  insufficient_funds: "Hết số dư",
  max_duration: "Hết giờ",
  upstream_closed: "Gián đoạn",
  error: "Lỗi",
  orphaned: "Gián đoạn",
  connection_lost: "Mất mạng",
};

export function sessionTitle(session: SessionSummary): string {
  return session.subject || session.viec_can_xem || "(không có chủ đề)";
}

/**
 * Một phiên trò chuyện thành một dòng.
 *
 * 🔴 Phiên chỉ tay có `fortune_type = 'tong_hop'` y như Bói Đê — thứ DUY NHẤT phân biệt
 * là `metadata.palm.reading_id`. Đọc nhãn hay đường dẫn chỉ theo `fortune_type` là gom
 * cả hai vào một rọ, và một phiên chỉ tay sẽ mở ra màn Bói Đê không có ảnh bàn tay nào.
 */
function sessionToFeedItem(session: SessionSummary): FeedItem {
  const palmReadingId = session.metadata?.palm?.reading_id;
  const boiDe = session.metadata?.boi_de;
  const messageCount = session.message_count ?? 0;
  const type: FeedType = palmReadingId
    ? "palm"
    : session.fortune_type === "tong_hop"
      ? "tong_hop"
      : (session.fortune_type ?? "tuvi");

  const details: string[] = [];
  if (boiDe?.topic_name) details.push(boiDe.topic_name);
  if (type === "iching") details.push(session.has_que ? "Đã gieo quẻ" : "Chưa gieo");
  // "Chưa luận giải" là tín hiệu quan trọng nhất của một quẻ 0 tin nhắn: nó nói cho
  // người gieo lúc hết tiền biết quẻ vẫn còn và vẫn luận giải được sau khi nạp.
  details.push(messageCount === 0 ? "Chưa luận giải" : `${messageCount} tin`);

  return {
    key: `session:${session.id}`,
    source: "session",
    id: session.id,
    category: messageCount >= 1 ? "dam-dao" : "luan-giai",
    type,
    title: sessionTitle(session),
    details,
    createdAt: session.created_at,
    palmReadingId: palmReadingId || undefined,
    boiDeSlug: boiDe?.topic_slug || undefined,
    renamable: true,
  };
}

/**
 * Tab lọc gửi cho `/v2/chat/sessions` ứng với một bộ lọc loại hình.
 *
 * `null` = loại hình ấy KHÔNG có phiên trò chuyện nào (Bói Live, đoán giờ sinh), tức là
 * bỏ hẳn nguồn phiên khỏi lượt tải chứ không phải gọi với `kind=all`.
 */
type SessionKindParam = "all" | "iching" | "tuvi" | "bazi" | "tong_hop" | "palm";

function sessionKindFor(type: TypeFilter): SessionKindParam | null {
  if (type === "all") return "all";
  if (type === "live" || type === "birth_time") return null;
  return type;
}

/**
 * Gói kết quả một nguồn thành `FeedPage`.
 *
 * ⚠️ `sealedUntil` của MỘT nguồn là mốc của bản ghi CUỐI trang vừa xin, và chỉ khi nguồn
 * ấy còn dữ liệu. Nguồn đã hết thì không chặn ai — nó không còn gì để chen vào giữa.
 */
function toFeedPage(items: FeedItem[], total: number, page: number): FeedPage {
  const hasMore = page * FEED_SOURCE_PAGE_SIZE < total;
  const last = items.at(-1);
  return {
    items,
    total,
    hasMore,
    sealedUntil: hasMore && last?.createdAt ? last.createdAt : null,
  };
}

async function fetchSessionPage(
  page: number,
  kind: SessionKindParam,
  q: string,
): Promise<FeedPage> {
  const data = await listSessions({
    page,
    limit: FEED_SOURCE_PAGE_SIZE,
    kind,
    q,
    sort: "newest",
    // ⚠️ BE đọc `min_messages: 1` là "≥1 tin nhắn HOẶC đã gieo quẻ" (xem
    // `HAVING ... OR s.hexagram_result IS NOT NULL` bên `session_service_db`). Nhờ vậy
    // quẻ đã gieo mà chưa luận giải vẫn về đủ — đừng đổi thành 0, phiên rỗng do bấm
    // nhầm sẽ tràn vào danh sách.
    minMessages: 1,
  });
  return toFeedPage(data.items.map(sessionToFeedItem), data.total, page);
}

async function fetchTuviPage(page: number): Promise<FeedPage> {
  const data = await listTuviCharts(page, FEED_SOURCE_PAGE_SIZE);
  return toFeedPage(
    data.items.map((chart) => ({
      key: `tuvi_chart:${chart.id}`,
      source: "tuvi_chart" as const,
      id: chart.id,
      category: "luan-giai" as const,
      type: "tuvi" as const,
      title: chart.ho_ten || "Lá số Tử Vi",
      details: [
        "Lá số",
        chart.nam_sinh ? `Sinh ${chart.nam_sinh}` : "",
        genderLabel(chart.gioi_tinh),
        chart.menh_cuc || "",
      ].filter(Boolean),
      createdAt: chart.created_at,
      renamable: false,
    })),
    data.total,
    page,
  );
}

async function fetchBaziPage(page: number): Promise<FeedPage> {
  const data = await listBaziCharts(page, FEED_SOURCE_PAGE_SIZE);
  return toFeedPage(
    data.items.map((chart) => ({
      key: `bazi_chart:${chart.id}`,
      source: "bazi_chart" as const,
      id: chart.id,
      category: "luan-giai" as const,
      type: "bazi" as const,
      title: chart.ho_ten || "Lá số Bát Tự",
      details: [
        "Lá số",
        chart.nam_sinh ? `Sinh ${chart.nam_sinh}` : "",
        genderLabel(chart.gioi_tinh),
        chart.day_master ? `Nhật chủ ${chart.day_master}` : "",
        // `has_reading` là dữ kiện RIÊNG của Bát Tự — danh sách lá số Tử Vi không trả
        // cờ tương đương, nên đừng bịa một nhãn tương tự cho Tử Vi.
        chart.has_reading ? "Đã khai mở" : "",
      ].filter(Boolean),
      createdAt: chart.created_at,
      renamable: false,
    })),
    data.total,
    page,
  );
}

async function fetchPalmPage(page: number): Promise<FeedPage> {
  const data = await listPalmReadings(page, FEED_SOURCE_PAGE_SIZE);
  return toFeedPage(
    data.items.map((reading) => {
      const names = reading.names.filter((name): name is string => Boolean(name && name.trim()));
      const isDraft = isPalmDraftItem(reading);
      return {
        key: `palm_reading:${reading.id}`,
        source: "palm_reading" as const,
        id: reading.id,
        category: "luan-giai" as const,
        type: "palm" as const,
        title: names.length ? names.join(" & ") : "Bài luận chỉ tay",
        details: [
          // Nhãn trạng thái thay chữ "Bài luận" cho lượt còn dở: dòng ấy đã bị trừ
          // tiền, để nó trông y hệt một bài đã xong là người xem bấm vào rồi ngơ ngác.
          (isDraft ? palmStageLabel(reading) : null) ?? "Bài luận",
          reading.persons_count > 1 ? `${reading.persons_count} người` : "",
          reading.price_charged > 0 ? formatVnd(reading.price_charged) : "",
        ].filter(Boolean),
        createdAt: reading.created_at,
        palmDraft: isDraft,
        renamable: false,
      };
    }),
    data.total,
    page,
  );
}

async function fetchLivePage(page: number): Promise<FeedPage> {
  const data = await listLiveSessions(page, FEED_SOURCE_PAGE_SIZE);
  return toFeedPage(
    data.items.map((session) => ({
      key: `live_session:${session.id}`,
      source: "live_session" as const,
      id: String(session.id),
      category: "luan-giai" as const,
      type: "live" as const,
      title: "Phiên Bói Live",
      details: [
        LIVE_END_REASON_LABELS[session.end_reason ?? ""] ?? session.status,
        formatDuration(session.duration_seconds),
        session.amount_charged > 0 ? formatVnd(session.amount_charged) : "",
      ].filter(Boolean),
      // Bói Live không có `created_at`; `started_at` mới là lúc phiên diễn ra. Phiên rớt
      // trước khi kịp bắt đầu thì không có mốc nào — để chuỗi rỗng và để tầng hiển thị
      // nói "Không rõ thời gian" thay vì dựng ra một ngày sai.
      createdAt: session.started_at ?? "",
      renamable: false,
    })),
    data.total,
    page,
  );
}

/** Nhãn độ tin cậy của một lượt đoán giờ sinh — giữ đồng bộ với màn kết quả. */
const BIRTH_TIME_CONFIDENCE_LABELS: Record<string, string> = {
  cao: "Tin cậy cao",
  trung_binh: "Tin cậy vừa",
  thap: "Tin cậy thấp",
};

async function fetchBirthTimePage(page: number): Promise<FeedPage> {
  const data = await listBirthTimeHistory(page, FEED_SOURCE_PAGE_SIZE);
  return toFeedPage(
    data.items.map((probe) => {
      const top = probe.summary?.top?.[0];
      // `status !== "done"` = đã trả tiền nhưng còn câu hỏi riêng chưa trả lời. Để nó
      // trông y hệt một lượt đã chấm xong là người xem bấm vào rồi ngơ ngác — cùng lý
      // lẽ với nhãn trạng thái của lượt chỉ tay còn dở.
      const unfinished = probe.status !== "done";
      return {
        key: `birth_time_probe:${probe.id}`,
        source: "birth_time_probe" as const,
        id: probe.id,
        // Một lượt đoán là KẾT QUẢ đã chấm, không mở ra phiên hỏi đáp nào ⇒ luôn
        // Luận giải. Xem `TYPES_BY_CATEGORY`.
        category: "luan-giai" as const,
        type: "birth_time" as const,
        title: probe.ho_ten || "Lượt đoán giờ sinh",
        details: [
          unfinished ? "Chưa trả lời hết" : top ? `Giờ ${top.chi}` : "Chưa có kết quả",
          !unfinished && top ? top.khung_gio : "",
          probe.summary?.do_tin_cay
            ? (BIRTH_TIME_CONFIDENCE_LABELS[probe.summary.do_tin_cay] ?? "")
            : "",
          probe.so_luot_hoi_them ? `Hỏi thêm ${probe.so_luot_hoi_them} lượt` : "",
          probe.price_charged > 0 ? formatVnd(probe.price_charged) : "",
        ].filter(Boolean),
        // ⚠️ `created_at` của BE là nullable — để chuỗi rỗng và để tầng hiển thị nói
        // "Không rõ thời gian" thay vì dựng ra một ngày sai (như phiên Bói Live).
        createdAt: probe.created_at ?? "",
        renamable: false,
      };
    }),
    data.total,
    page,
  );
}

/**
 * Những nguồn cần gọi cho một bộ lọc.
 *
 * 🔴 Bộ lọc LOẠI HÌNH chạy ở đây (tức ở server, qua việc chọn nguồn + tham số `kind`),
 * KHÔNG lọc lại trên danh sách đã tải: với 10 dòng mỗi nguồn mỗi lượt, lọc ở client chỉ
 * soi được chừng ấy dòng và sẽ nói "không có" về những bản ghi vẫn còn nguyên trong tài
 * khoản. Bộ lọc TAG thì ngược lại — xem `filterFeedByCategory`.
 */
function fetchersFor(
  category: CategoryFilter,
  type: TypeFilter,
  q: string,
): ((page: number) => Promise<FeedPage>)[] {
  const fetchers: ((page: number) => Promise<FeedPage>)[] = [];

  // 🔴 TAG cũng cắt bớt nguồn ở đây, dù nó vẫn phải lọc lại ở client cho phần dư.
  // Không cắt thì lọc "Đàm đạo" vẫn đi xin các nguồn bài luận — thứ không bao giờ là đàm
  // đạo — rồi vứt sạch kết quả; và lọc "Luận giải" sẽ lôi về cả trăm phiên chat chỉ để bỏ
  // đi, kéo theo hàng chục lượt gọi mạng thừa mỗi lần sang trang.
  const sessionKind =
    category === "luan-giai"
      ? // Trong các phiên chat, CHỈ quẻ Kinh Dịch chưa luận giải mới mang tag Luận giải.
        type === "all" || type === "iching"
        ? ("iching" as const)
        : null
      : sessionKindFor(type);
  if (sessionKind) fetchers.push((page) => fetchSessionPage(page, sessionKind, q));

  // Lá số, bài chỉ tay, phiên Bói Live và lượt đoán giờ sinh không bao giờ là Đàm đạo.
  if (category !== "dam-dao") {
    if (type === "all" || type === "tuvi") fetchers.push(fetchTuviPage);
    if (type === "all" || type === "bazi") fetchers.push(fetchBaziPage);
    if (type === "all" || type === "palm") fetchers.push(fetchPalmPage);
    if (type === "all" || type === "live") fetchers.push(fetchLivePage);
    if (type === "all" || type === "birth_time") fetchers.push(fetchBirthTimePage);
  }
  return fetchers;
}

/**
 * Một lượt tải.
 *
 * 🔴 `allSettled` chứ không `all`: sáu nguồn là sáu dịch vụ độc lập. Một nguồn hỏng
 * (Bói Live chưa bật ở môi trường ấy, chỉ tay lỗi 500…) mà làm rỗng cả danh sách thì
 * người xem mất luôn các nguồn còn lại — tệ hơn hẳn việc thiếu một nhóm.
 */
export async function fetchFeedPage(
  { category, type, q }: { category: CategoryFilter; type: TypeFilter; q: string },
  page: number,
): Promise<FeedPage> {
  const fetchers = fetchersFor(category, type, q);
  if (!fetchers.length) return { items: [], total: 0, hasMore: false, sealedUntil: null };
  const results = await Promise.allSettled(fetchers.map((fetcher) => fetcher(page)));

  // Chỉ có một nguồn mà nguồn ấy hỏng thì KHÔNG có gì để hiện — ném lại lỗi để trang
  // báo đúng sự thật thay vì nói "chưa có gì".
  if (fetchers.length === 1 && results[0].status === "rejected") {
    throw results[0].reason;
  }

  const pages = results
    .filter((result): result is PromiseFulfilledResult<FeedPage> => result.status === "fulfilled")
    .map((result) => result.value);

  // Ranh giới an toàn của lượt này = mốc MỚI NHẤT trong các đuôi của những nguồn còn dữ
  // liệu. Nguồn nào còn hàng thì nó vẫn có thể chen bản ghi vào bên DƯỚI đuôi của nó, nên
  // phần gộp chỉ hoàn chỉnh tới cái đuôi muộn nhất trong số ấy.
  const boundaries = pages
    .map((entry) => entry.sealedUntil)
    .filter((value): value is string => Boolean(value))
    .map((value) => Date.parse(value))
    .filter((value) => !Number.isNaN(value));

  return {
    items: pages.flatMap((entry) => entry.items),
    total: pages.reduce((sum, entry) => sum + entry.total, 0),
    hasMore: pages.some((entry) => entry.hasMore),
    sealedUntil: boundaries.length ? new Date(Math.max(...boundaries)).toISOString() : null,
  };
}

/**
 * Cắt danh sách gộp tại ranh giới an toàn.
 *
 * 🔴 Gọi hàm này TRƯỚC khi chia trang, không phải sau. Bỏ nó đi thì một bản ghi chưa tải
 * xong sẽ chen vào giữa ở lượt tải sau và đẩy mọi thứ dịch một ô — người xem bấm "Sau"
 * rồi "Trước" sẽ thấy trang 1 khác lúc nãy.
 *
 * `boundary === null` nghĩa là mọi nguồn đã hết: giữ tất cả, kể cả bản ghi không đọc được
 * mốc thời gian (phiên Bói Live rớt trước khi kịp bắt đầu).
 */
export function sealFeed(items: FeedItem[], boundary: string | null): FeedItem[] {
  if (!boundary) return items;
  const limit = Date.parse(boundary);
  if (Number.isNaN(limit)) return items;
  return items.filter((item) => {
    const at = Date.parse(item.createdAt);
    return !Number.isNaN(at) && at >= limit;
  });
}

/**
 * Lọc theo TAG.
 *
 * ⚠️ Đây là bộ lọc duy nhất chạy ở CLIENT, và có lý do: tag suy từ số tin nhắn của từng
 * bản ghi, mà `/v2/chat/sessions` không có tham số nào tách "phiên đã có tin nhắn" khỏi
 * "quẻ đã gieo chưa luận" — nó gộp hai thứ ấy trong cùng một mệnh đề `HAVING`. Vì nó
 * chạy ở client, trang KHÔNG được hiện tổng số của bộ lọc này (con số ấy sẽ là số của
 * phần đã tải, không phải của tài khoản) — xem chỗ dùng `total` bên trang Lịch sử.
 */
export function filterFeedByCategory(items: FeedItem[], category: CategoryFilter): FeedItem[] {
  return category === "all" ? items : items.filter((item) => item.category === category);
}

/**
 * Lọc phần đã tải theo từ khoá cho những nguồn KHÔNG tìm được ở server.
 *
 * ⚠️ Phiên trò chuyện đã được `/v2/chat/sessions?q=` lọc sẵn ở server nên bỏ qua ở đây.
 * Bốn nguồn còn lại không có tham số tìm kiếm, nên chỉ soi được phần đã tải — bấm
 * "Xem thêm" là soi rộng thêm. Trang phải nói rõ điều đó khi có từ khoá.
 */
export function filterFeedBySearch(items: FeedItem[], q: string): FeedItem[] {
  const needle = normalizeSearchText(q.trim());
  if (!needle) return items;
  return items.filter(
    (item) => item.source === "session" || normalizeSearchText(item.title).includes(needle),
  );
}

/**
 * Sắp lại TOÀN BỘ danh sách đã tải theo thời gian.
 *
 * ⚠️ Phải chạy trên cả danh sách gộp sau mỗi lượt "Xem thêm", không phải trên từng
 * trang: sáu nguồn phân trang độc lập nên nối đuôi thuần tuý cho ra thứ tự sai.
 */
export function sortFeedByDate(items: FeedItem[]): FeedItem[] {
  // Bản ghi thiếu mốc thời gian (phiên live rớt trước khi bắt đầu) luôn xuống cuối:
  // `NaN` trong so sánh làm thứ tự phụ thuộc thuật toán sort, không phải dữ liệu.
  return [...items].sort((left, right) => {
    const leftTime = Date.parse(left.createdAt);
    const rightTime = Date.parse(right.createdAt);
    if (Number.isNaN(leftTime) && Number.isNaN(rightTime)) return 0;
    if (Number.isNaN(leftTime)) return 1;
    if (Number.isNaN(rightTime)) return -1;
    return rightTime - leftTime;
  });
}

/** Xoá một bản ghi ở đúng nguồn của nó. */
export async function deleteFeedItem(item: FeedItem): Promise<void> {
  switch (item.source) {
    case "session":
      await deleteSession(item.id);
      return;
    case "tuvi_chart":
      await deleteTuviChart(item.id);
      return;
    case "bazi_chart":
      await deleteBaziChart(item.id);
      return;
    case "palm_reading":
      await deletePalmReading(item.id);
      return;
    case "live_session":
      await deleteLiveSession(Number(item.id));
      return;
    case "birth_time_probe":
      await deleteBirthTimeHistory(item.id);
      return;
  }
}

/**
 * Câu cảnh báo trước khi xoá — nói ĐÚNG thứ sắp mất ở từng nguồn.
 *
 * Người xem đã trả tiền cho phần lớn những bản ghi này; một câu chung chung ("Xoá mục
 * này?") không cho họ biết là mất cả bài luận đã mua.
 */
export function deleteWarningFor(item: FeedItem): string {
  switch (item.source) {
    case "session":
      // Phiên chỉ tay xoá đi là mất luôn BÀI LUẬN và ẢNH BÀN TAY, không chỉ mất cuộc
      // trò chuyện — máy chủ xoá cả hai bản ghi.
      if (item.palmReadingId) {
        return `Cả bài luận “${item.title}”, ảnh bàn tay đã gửi và phần hỏi đáp kèm theo sẽ bị xoá. Hành động này không thể hoàn tác và không hoàn tiền.`;
      }
      if (item.type === "iching") {
        return `Quẻ “${item.title}” và toàn bộ lời luận giải kèm theo sẽ bị xoá. Hành động này không thể hoàn tác.`;
      }
      return `Phiên “${item.title}” và lịch sử hỏi đáp liên quan sẽ bị xoá. Hành động này không thể hoàn tác.`;
    case "tuvi_chart":
      return `Lá số Tử Vi “${item.title}” và phần khai mở đã trả tiền sẽ bị xoá. Hành động này không thể hoàn tác và không hoàn tiền.`;
    case "bazi_chart":
      return `Lá số Bát Tự “${item.title}” và phần khai mở đã trả tiền sẽ bị xoá. Hành động này không thể hoàn tác và không hoàn tiền.`;
    case "palm_reading":
      return `Bài luận chỉ tay “${item.title}”, ảnh bàn tay đã gửi và phần hỏi đáp kèm theo sẽ bị xoá. Hành động này không thể hoàn tác và không hoàn tiền.`;
    case "live_session":
      return "Bản ghi phiên Bói Live này (lời thoại, kết quả gieo quẻ/lá số trong phiên) sẽ bị xoá. Hành động này không thể hoàn tác.";
    case "birth_time_probe":
      // Xoá một lượt là mất luôn bộ câu trả lời đã lưu, tức mất cả khả năng phỏng vấn
      // thêm trên chính lượt ấy.
      return `Lượt đoán giờ sinh của “${item.title}” cùng toàn bộ câu trả lời đã lưu sẽ bị xoá, và bạn không phỏng vấn thêm trên lượt này được nữa. Hành động này không thể hoàn tác.`;
  }
}

/** Các queryKey phải bỏ cache sau khi xoá — danh sách ấy còn hiện ở trang tính năng. */
export function relatedQueryKeys(item: FeedItem): string[] {
  const keys = ["history-feed"];
  switch (item.source) {
    case "session":
      keys.push("boide-sessions");
      // Phiên chỉ tay và bài luận của nó là hai bản ghi ở hai danh sách; máy chủ xoá cả
      // hai, nên danh sách bên trang Bói chỉ tay cũng phải bỏ cache.
      if (item.palmReadingId) keys.push("palm-readings");
      break;
    case "tuvi_chart":
      keys.push("tuvi-charts");
      break;
    case "bazi_chart":
      keys.push("bazi-charts");
      break;
    case "palm_reading":
      keys.push("palm-readings", "boide-sessions");
      break;
    case "live_session":
      keys.push("live-sessions");
      break;
    case "birth_time_probe":
      // Khối "Lịch sử đoán giờ sinh" trên trang /doan-gio-sinh đọc cùng dữ liệu này.
      keys.push("birth-time-history");
      break;
  }
  return keys;
}
